import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Truck, Settings, KeyRound, MapPin, Wrench, SprayCan, Users, ClipboardCheck, Package, ShieldCheck, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SectionBubbles from '../components/SectionBubbles';
import SEO from '../components/SEO';
import { services } from '../data/mock';
import { faqData } from '../data/galleryImages';

const IMG_VAN = "https://customer-assets.emergentagent.com/job_8e095e6e-5bce-4e1e-981b-6fba51b00406/artifacts/0x1298ui_WhatsApp%20Image%202026-04-13%20at%2018.56.26%20%282%29.jpeg";
const IMG_TECH = "https://customer-assets.emergentagent.com/job_8e095e6e-5bce-4e1e-981b-6fba51b00406/artifacts/uh6md80l_WhatsApp%20Image%202026-04-13%20at%2018.56.26%20%281%29.jpeg";
const IMG_FLEET = "https://customer-assets.emergentagent.com/job_8e095e6e-5bce-4e1e-981b-6fba51b00406/artifacts/fjozgdaa_WhatsApp%20Image%202026-04-13%20at%2018.56.26.jpeg";

const capIcons = [Truck, Wrench, Settings, SprayCan, Users, KeyRound];
const movesIcons = [Truck, Wrench, SprayCan, Users, ClipboardCheck, ShieldCheck];

const Services = () => {
  const { t } = useTranslation();
  const fleetServices = services.filter(s => s.id <= 8);

  return (
    <div className="page-wrapper">
      <SEO 
        title={t('seo.services.title')}
        description={t('seo.services.description')}
        keywords={t('seo.services.keywords')}
        canonical="https://dsotocleaning.com/services"
      />
      <Navigation />
      
      <section className="page-hero">
        <div className="container">
          <h1 className="page-hero-title">{t('services.hero_title')}</h1>
          <p className="page-hero-description">{t('services.hero_desc')}</p>
        </div>
      </section>

      {/* 01 — MOBILE FLEET OPERATIONS */}
      <section className="svc-section" id="mobile-service" data-testid="mobile-fleet-section">
        <div className="svc-section-header svc-dark">
          <div className="container">
            <div className="svc-number-badge">{t('services.s01_badge')}</div>
            <div className="svc-eyebrow">{t('services.s01_eyebrow')}</div>
            <h2 className="svc-headline">{t('services.s01_headline')}</h2>
            <p className="svc-lead">{t('services.s01_lead')}</p>
            <p className="svc-body-white">{t('services.s01_p1')}</p>
            <p className="svc-body-white">{t('services.s01_p2')}</p>
            <p className="svc-emphasis">{t('services.s01_emphasis')}</p>
          </div>
        </div>

        {/* Cinematic Video Hero */}
        <div className="svc-cinema" data-testid="mobile-video-section">
          <div className="svc-cinema-video">
            <iframe
              src="https://www.youtube.com/embed/4Daot4ehdMU?autoplay=1&mute=1&controls=0&loop=1&playlist=4Daot4ehdMU&playsinline=1&rel=0&modestbranding=1&disablekb=1&showinfo=0&iv_load_policy=3&fs=0"
              title="Mobile Service D Soto Cleaning"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="svc-cinema-iframe"
              loading="lazy"
              data-testid="mobile-service-video"
            />
          </div>
          <div className="svc-cinema-overlay" />
          <div className="svc-cinema-content">
            <div className="svc-cinema-eyebrow">{t('services.s01_video_eyebrow')}</div>
            <h3 className="svc-cinema-title">{t('services.s01_video_title')}</h3>
            <p className="svc-cinema-desc">{t('services.s01_video_desc')}</p>
            <div className="svc-cinema-scroll">
              <span>{t('services.s01_video_cta')}</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M4 9l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
          </div>
          <div className="svc-cinema-fade" />
        </div>

        {/* Seamless transition into capabilities */}
        <div className="svc-capabilities">
          <div className="container">
            <h3 className="svc-sub-headline">{t('services.s01_cap_title')}</h3>
            <p className="svc-sub-desc">{t('services.s01_cap_desc')}</p>
            <div className="svc-cap-grid">
              {t('services.s01_caps', { returnObjects: true }).map((cap, i) => {
                const Icon = capIcons[i];
                return (
                  <div key={i} className="svc-cap-item" data-testid={`mobile-cap-${i + 1}`}>
                    <div className="svc-cap-num">{String(i + 1).padStart(2, '0')}</div>
                    <div className="svc-cap-icon"><Icon size={22} /></div>
                    <h4 className="svc-cap-title">{cap.title}</h4>
                    <p className="svc-cap-desc">{cap.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="svc-alternating">
          <div className="container">
            <div className="svc-alt-row">
              <div className="svc-alt-img"><img src={IMG_VAN} alt="Mobile fleet van" data-testid="mobile-fleet-img-1" /></div>
              <div className="svc-alt-text">
                <h4>{t('services.s01_alt1_title')}</h4>
                <p>{t('services.s01_alt1_desc')}</p>
              </div>
            </div>
            <div className="svc-alt-row svc-alt-reverse">
              <div className="svc-alt-img"><img src={IMG_TECH} alt="Technician with equipment" data-testid="mobile-fleet-img-2" /></div>
              <div className="svc-alt-text">
                <h4>{t('services.s01_alt2_title')}</h4>
                <p>{t('services.s01_alt2_desc')}</p>
              </div>
            </div>
            <div className="svc-alt-row">
              <div className="svc-alt-img"><img src={IMG_FLEET} alt="Fleet operations" data-testid="mobile-fleet-img-3" /></div>
              <div className="svc-alt-text">
                <h4>{t('services.s01_alt3_title')}</h4>
                <p>{t('services.s01_alt3_desc')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="svc-moves-section" data-testid="what-moves-section">
          <div className="container">
            <h3 className="svc-sub-headline text-center">{t('services.s01_moves_title')}</h3>
            <div className="svc-moves-grid">
              {t('services.s01_moves', { returnObjects: true }).map((item, i) => {
                const Icon = movesIcons[i];
                return (
                  <div key={i} className="svc-moves-item">
                    <Icon size={20} className="svc-moves-icon" />
                    <div>
                      <h5 className="svc-moves-title">{item.title}</h5>
                      <p className="svc-moves-desc">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="svc-moves-footer">{t('services.s01_moves_footer')}</p>
          </div>
        </div>

        <div className="svc-brand-statement">
          <div className="container text-center">
            <p className="svc-brand-line-1">{t('services.s01_brand_1')}</p>
            <p className="svc-brand-line-2">{t('services.s01_brand_2')}</p>
          </div>
        </div>
        <div className="svc-section-cta">
          <div className="container text-center">
            <Link to="/contact">
              <Button size="lg" className="cta-button-orange" data-testid="mobile-fleet-cta-btn">
                {t('services.s01_cta')} <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 02 — FLEET CLEANING SERVICES */}
      <section className="svc-section" id="fleet-services">
        <div className="svc-section-header-light">
          <SectionBubbles />
          <div className="container">
            <div className="svc-number-badge-outline">02</div>
            <h2 className="svc-headline-dark">{t('services.s02_title')}</h2>
            <p className="svc-sub-desc">{t('services.s02_desc')}</p>
          </div>
        </div>
        <div className="svc-services-grid-wrap">
          <div className="container">
            <div className="svc-services-compact">
              {fleetServices.map((service, index) => (
                <div key={service.id} className="svc-service-item" data-testid={`service-${service.id}`}>
                  <span className="svc-service-num">{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h4 className="svc-service-name">{service.title}</h4>
                    <p className="svc-service-desc">{service.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 03 — SPECIALIZED SERVICES */}
      <section className="svc-section" id="specialized">
        <div className="svc-section-header svc-dark">
          <div className="container">
            <div className="svc-number-badge">03</div>
            <h2 className="svc-headline">{t('services.s03_title')}</h2>
            <p className="svc-body-white">{t('services.s03_desc')}</p>
          </div>
        </div>
        <div className="baby-seat-section section-padding">
          <div className="container">
            <div className="baby-seat-content">
              <div className="baby-seat-images">
                <div className="baby-seat-img-grid">
                  <img src="https://customer-assets.emergentagent.com/job_8e095e6e-5bce-4e1e-981b-6fba51b00406/artifacts/b3ekps09_carwash-removing-of-dust-and-dirt-from-child-seat-2026-01-06-10-07-27-utc.jpg" alt="Baby car seat cleaning" className="baby-seat-img" data-testid="baby-seat-img-1" />
                  <img src="https://customer-assets.emergentagent.com/job_8e095e6e-5bce-4e1e-981b-6fba51b00406/artifacts/izalu1z2_carwash-removing-of-dust-and-dirt-from-child-seat-2026-01-06-10-41-21-utc.jpg" alt="Baby car seat detail" className="baby-seat-img" data-testid="baby-seat-img-2" />
                </div>
              </div>
              <div className="baby-seat-text">
                <h3 className="svc-sub-headline" style={{marginBottom: '1rem'}}>{t('services.s03_baby_title')}</h3>
                <p className="baby-seat-description">{t('services.s03_baby_p1')}</p>
                <p className="baby-seat-description">{t('services.s03_baby_p2')}</p>
                <div className="baby-seat-stjude">
                  <img src="https://customer-assets.emergentagent.com/job_8e095e6e-5bce-4e1e-981b-6fba51b00406/artifacts/loinawbx_St_Jude_logo_PNG17.png" alt="St. Jude" className="stjude-logo" data-testid="stjude-logo" />
                  <span className="stjude-label">{t('services.s03_baby_donor')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 04 — QUICK TURNAROUND */}
      <section className="svc-section" id="quick-turnaround">
        <div className="svc-quick-section">
          <SectionBubbles />
          <div className="container">
            <div className="svc-quick-content">
              <div className="svc-quick-text">
                <div className="svc-number-badge-outline">04</div>
                <h2 className="svc-headline-dark">{t('services.s04_title')}</h2>
                <p className="svc-body-dark">{t('services.s04_desc')}</p>
                <p className="svc-body-dark">{t('services.s04_p2')}</p>
                <div className="svc-quick-features">
                  {t('services.s04_feats', { returnObjects: true }).map((feat, i) => {
                    const icons = [Clock, Package, Users, ClipboardCheck];
                    const Icon = icons[i];
                    return <div key={i} className="svc-quick-feat"><Icon size={18} /><span>{feat}</span></div>;
                  })}
                </div>
              </div>
              <div className="svc-quick-img">
                <img src="https://customer-assets.emergentagent.com/job_3af646f4-3c0e-45dd-86e1-ef70869af78b/artifacts/p99mbmel_10.jpg" alt="Quick Turnaround" className="image-rounded" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 05 — SCALE, QC & COMPLIANCE */}
      <section className="svc-section" id="quality-compliance">
        <div className="svc-section-header svc-dark">
          <div className="container">
            <div className="svc-number-badge">05</div>
            <h2 className="svc-headline">{t('services.s05_title')}</h2>
            <p className="svc-body-white">{t('services.s05_desc')}</p>
          </div>
        </div>
        <div className="svc-quality-wrap section-padding">
          <div className="container">
            <div className="svc-quality-grid">
              <div className="svc-quality-text">
                <h3 className="svc-sub-headline">{t('services.s05_sub_title')}</h3>
                <p className="svc-body-dark">{t('services.s05_sub_desc')}</p>
                <div className="svc-quality-list">
                  {t('services.s05_standards', { returnObjects: true }).map((standard, i) => (
                    <div key={i} className="svc-quality-item">
                      <CheckCircle2 size={18} className="svc-check" /><span>{standard}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="svc-compliance-cards">
                <div className="svc-compliance-card"><h4>{t('services.s05_osha_title')}</h4><p>{t('services.s05_osha_desc')}</p></div>
                <div className="svc-compliance-card"><h4>{t('services.s05_env_title')}</h4><p>{t('services.s05_env_desc')}</p></div>
                <div className="svc-compliance-card"><h4>{t('services.s05_qa_title')}</h4><p>{t('services.s05_qa_desc')}</p></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 06 — ENTERPRISE FAQ */}
      <section className="svc-section" id="faq">
        <div className="svc-section-header-light svc-faq-wrap">
          <div className="container">
            <div className="svc-number-badge-outline">06</div>
            <h2 className="svc-headline-dark">{t('services.s06_title')}</h2>
            <p className="svc-sub-desc">{t('services.s06_desc')}</p>
            <div className="faq-container" style={{marginTop: '3rem'}}>
              <Accordion type="single" collapsible className="faq-accordion">
                {faqData.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="faq-item">
                    <AccordionTrigger className="faq-question">{faq.question}</AccordionTrigger>
                    <AccordionContent className="faq-answer">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-section section-padding">
        <div className="container text-center">
          <h2 className="section-title text-white">{t('services.final_cta_1')}</h2>
          <p className="section-description text-white centered-text mb-2" style={{fontSize: '1.35rem', fontWeight: 600, color: '#F7941D'}}>{t('services.final_cta_2')}</p>
          <p className="section-description text-white centered-text mb-8">{t('services.final_cta_desc')}</p>
          <Link to="/contact"><Button size="lg" className="cta-button-white">{t('services.final_cta_btn')} <ArrowRight className="ml-2" /></Button></Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
